import React, { useState, useEffect } from 'react';
import { useAuth } from '@/lib';
import { UserBio } from '@orbit/shared/types';
import { createBusinessIdeasPrompt, cleanUserBioData } from '@orbit/shared';
import OnboardingStepWrapper from './onboarding/OnboardingStepWrapper';
import { useOnboarding } from '../context/OnboardingContext';
import LoadingPage from './LoadingPage';
import { BusinessIdea } from './onboarding/onboarding.types';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { mockEnhanceBusinessIdea } from '../services/mockAIService';
import { Box, Typography, Button, Card, CardContent, Chip, Tooltip } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { shouldUseMockData } from '../config/dev';

interface GenerateBusinessIdeasProps {
  // Entry point A: Onboarding flow
  isOnboarding?: boolean;
  // Entry point B: Dashboard or other future entry points
  userBio?: UserBio;
}

const GenerateBusinessIdeas: React.FC<GenerateBusinessIdeasProps> = ({
  isOnboarding = false,
  userBio: externalUserBio,
}) => {
  const { user, getApp } = useAuth();
  const { state, saveUserBioToFirestore, setData, setStep } = useOnboarding();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [userBio, setUserBio] = useState<UserBio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [businessIdeas, setBusinessIdeas] = useState<BusinessIdea[]>([]);

  // Initialize business ideas from context if available
  useEffect(() => {
    if (state.data.generatedBusinessIdeas && state.data.generatedBusinessIdeas.length > 0) {
      setBusinessIdeas(state.data.generatedBusinessIdeas);
    }
  }, [state.data.generatedBusinessIdeas]);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        setLoading(true);
        setError(null);

        if (externalUserBio) {
          // Entry point B: Direct userBio provided
          setUserBio(externalUserBio);
          setLoading(false);
        } else if (isOnboarding && state.data.userBio) {
          // Entry point A: Onboarding flow - user bio is already in context
          // Clean the user bio data using the imported function
          const cleanedUserBio = cleanUserBioData(state.data.userBio);
          setUserBio(cleanedUserBio);
          setLoading(false);

          // Save to Firestore in the background (don't block UI)
          try {
            const app = getApp();
            await saveUserBioToFirestore(user!.id, state.data.userBio, app);
          } catch (err) {
            console.error('Error saving user bio to Firestore (non-blocking):', err);
            // Don't show error to user since this is background operation
          }
        } else {
          setError('No user bio data available');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error initializing GenerateBusinessIdeas:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        setLoading(false);
      }
    };

    initializeComponent();
  }, [isOnboarding, externalUserBio, state.data.userBio, user, getApp, saveUserBioToFirestore]);

  // Generate business ideas when userBio is available
  useEffect(() => {
    if (
      userBio &&
      !generating &&
      businessIdeas.length === 0 &&
      !state.data.generatedBusinessIdeas
    ) {
      generateBusinessIdeas();
    }
  }, [userBio, businessIdeas.length, state.data.generatedBusinessIdeas]);

  const generateBusinessIdeas = async () => {
    if (!userBio) return;

    try {
      setGenerating(true);
      setError(null);

      if (shouldUseMockData()) {
        // Use mock data in development
        console.log('🤖 Using mock AI service for development');
        const result = await mockEnhanceBusinessIdea({
          userInput: userBio,
          isMultipleIdeas: true,
        });

        if (result.data.success && result.data.businessIdeas) {
          // Transform the mock response into BusinessIdea objects
          const ideas: BusinessIdea[] = result.data.businessIdeas.map(
            (idea: any, index: number) => ({
              ...idea,
              id: '', // Will be set by Firestore
              userId: user!.id,
              ownerId: user!.id,
              collaboratorIds: [user!.id],
            }),
          );

          setBusinessIdeas(ideas);
          // Cache the generated ideas in context
          setData({ generatedBusinessIdeas: ideas });
        } else {
          throw new Error(result.data.error || 'Failed to generate business ideas');
        }
      } else {
        // Use real OpenAI API in production
        const app = getApp();
        const functions = getFunctions(app);
        const enhanceBusinessIdea = httpsCallable(functions, 'enhanceBusinessIdea');

        // Create a prompt for generating multiple business ideas
        const businessIdeasPrompt = createBusinessIdeasPrompt(userBio, 3);

        const result = await enhanceBusinessIdea({
          userInput: businessIdeasPrompt,
          isMultipleIdeas: true,
        });

        const data = result.data as any;

        console.log('Raw response data:', data);

        // Handle the response structure from Firebase Functions
        // The response might be wrapped in a 'result' object
        const responseData = data.result || data;

        console.log('Processed response data:', responseData);

        if (responseData.success && responseData.businessIdeas) {
          // Transform the AI response into BusinessIdea objects
          const ideas: BusinessIdea[] = responseData.businessIdeas.map(
            (idea: any, index: number) => ({
              id: '', // Will be set by Firestore
              name: idea.name || `Business Idea ${index + 1}`,
              summary: idea.summary || '',
              description: idea.description || '',
              stage: idea.stage || 'idea',
              businessModel: idea.businessModel || 'saas',
              monetization: idea.monetization || 'subscription',
              problemStatement: idea.problemStatement || '',
              solutionOverview: idea.solutionOverview || '',
              targetAudience: idea.targetAudience || '',
              industry: idea.industry || [],
              buildApproach: idea.buildApproach || ['web-app'],
              differentiator: idea.differentiator || '',
              source: 'ai',
              userId: user!.id,
              ownerId: user!.id,
              collaboratorIds: [user!.id],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }),
          );

          setBusinessIdeas(ideas);
          // Cache the generated ideas in context
          setData({ generatedBusinessIdeas: ideas });
        } else {
          throw new Error(data.error || 'Failed to generate business ideas');
        }
      }
    } catch (err) {
      console.error('Error generating business ideas:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate business ideas');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectIdea = (idea: BusinessIdea) => {
    // Save the selected idea to context and set processing type
    setData({
      businessIdea: idea,
      processingType: 'selected-from-generated',
    });

    // Route to the review page
    setStep('business-idea-review');
  };

  const handleRegenerate = () => {
    // Clear cached ideas and regenerate
    console.log('🔄 Clearing cached business ideas and regenerating');
    setData({ generatedBusinessIdeas: undefined });
    setBusinessIdeas([]);
    generateBusinessIdeas();
  };

  // Show loading state during initialization
  if (loading) {
    return (
      <LoadingPage
        subtitle="Based on what I know about you, I'm coming up with some relevant business ideas you can explore, shape, and launch into orbit. Please be patient while I think"
        size="medium"
      />
    );
  }

  // Show generating state
  if (generating) {
    return (
      <LoadingPage
        title="Generating Business Ideas"
        subtitle="Our AI is analyzing your profile and creating personalized business opportunities"
        size="large"
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <OnboardingStepWrapper
        title="Something went wrong"
        subtitle="We encountered an error while processing your information"
        showBackButton={true}
      >
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto">
            <svg
              width="32"
              height="32"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-white"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </div>
          <p className="text-gray-300">{error}</p>
          <Button
            variant="contained"
            onClick={generateBusinessIdeas}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Try Again
          </Button>
        </div>
      </OnboardingStepWrapper>
    );
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <OnboardingStepWrapper
        title="Your Business Ideas"
        subtitle="Based on your profile, here are some personalized business opportunities"
        showBackButton={true}
      >
        <Box className="max-w-6xl mx-auto space-y-8">
          {businessIdeas.length > 0 && (
            <Box className="flex justify-end">
              <Button
                variant="outlined"
                onClick={handleRegenerate}
                className="border-gray-600 text-gray-300 hover:border-gray-500"
                startIcon={<Refresh />}
              >
                Regenerate Ideas
              </Button>
            </Box>
          )}
          {businessIdeas.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {businessIdeas.map((idea, index) => (
                <Card
                  key={idea.id}
                  className="bg-app-surface border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer h-full"
                  sx={{ borderRadius: '8px' }}
                >
                  <CardContent className="p-6 h-full flex flex-col">
                    <Box className="flex items-start justify-between mb-4">
                      <Typography variant="h6" className="text-white font-bold">
                        {idea.name}
                      </Typography>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        className="bg-blue-600 text-white"
                      />
                    </Box>

                    <Typography variant="body2" className="text-gray-300 mb-4 flex-grow">
                      {idea.summary}
                    </Typography>

                    <Box className="space-y-3">
                      {/* Industry Tags */}
                      {idea.industry.length > 0 && (
                        <Box>
                          <Typography variant="caption" className="text-gray-400 block mb-1">
                            Industry
                          </Typography>
                          <Box className="flex flex-wrap gap-1">
                            {idea.industry.slice(0, 2).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag.replace('-', ' ')}
                                size="small"
                                className="bg-gray-700 text-gray-300 text-xs"
                              />
                            ))}
                            {idea.industry.length > 2 && (
                              <Tooltip
                                title={idea.industry
                                  .slice(2)
                                  .map((tag) => tag.replace('-', ' '))
                                  .join(', ')}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  label={`+${idea.industry.length - 2}`}
                                  size="small"
                                  className="bg-gray-600 text-gray-400 text-xs cursor-help"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Business Model */}
                      <Box>
                        <Typography variant="caption" className="text-gray-400 block mb-1">
                          Model
                        </Typography>
                        <Chip
                          label={idea.businessModel.replace('-', ' ')}
                          size="small"
                          className="bg-green-600 text-white text-xs"
                        />
                      </Box>

                      {/* Revenue Model */}
                      {idea.monetization.length > 0 && (
                        <Box className="pb-6">
                          <Typography variant="caption" className="text-gray-400 block mb-1">
                            Revenue
                          </Typography>
                          <Chip
                            label={idea.monetization.replace('-', ' ')}
                            size="small"
                            className="bg-purple-600 text-white text-xs"
                          />
                        </Box>
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleSelectIdea(idea)}
                      sx={{
                        background: 'linear-gradient(135deg, #3987DE 0%, #2D6BC7 100%)',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: '13px',
                        textTransform: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        marginTop: '16px',
                        minHeight: '36px',
                        boxShadow: '0 4px 12px rgba(57, 135, 222, 0.3)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2D6BC7 0%, #1E4A8C 100%)',
                          boxShadow: '0 6px 20px rgba(57, 135, 222, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      Explore This Idea
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-white"
                >
                  <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <p className="text-gray-300">
                No business ideas generated yet. Click the button below to generate some ideas.
              </p>
              <Button
                variant="contained"
                onClick={generateBusinessIdeas}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Generate Business Ideas
              </Button>
            </div>
          )}
        </Box>
      </OnboardingStepWrapper>
    </Box>
  );
};

export default GenerateBusinessIdeas;
